import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/common/Breadcrumb';
import {
    IconMessageCircle, IconLoader, IconChevronRight,
    IconCheck, IconSearch,
} from '@tabler/icons-react';
import { useTeam360Requests } from '../../hooks/usePerformance';

function formatDate(d) {
    if (!d) return '–';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getInitials(name = '') {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const AVATAR_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4', '#3b82f6'];
function getAvatarColor(name = '') {
    return AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];
}

const STATUS_DOT   = { draft: '#FC985E', active: 'var(--success)', closed: 'var(--success)' };
const STATUS_LABEL = { draft: 'Draft',   active: 'Active',         closed: 'Closed' };
const STATUS_CLASS = {
    draft:  'bg-warning/10 text-warning border-warning/20',
    active: 'bg-success/10 text-success border-success/20',
    closed: 'bg-success/10 text-success border-success/20',
};

function StatusPill({ status }) {
    return (
        <span className={`inline-flex items-center gap-5 px-8 py-3 rounded-full text-[11px] font-semibold border whitespace-nowrap ${STATUS_CLASS[status] || STATUS_CLASS.draft}`}>
            <span className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_DOT[status] || STATUS_DOT.draft }} />
            {STATUS_LABEL[status] || status}
        </span>
    );
}

export default function Feedback360HR() {
    const navigate = useNavigate();
    const [cycleName, setCycleName] = useState('');
    const [search, setSearch]       = useState('');

    const { data: requests = [], isLoading } = useTeam360Requests(cycleName);

    const filtered = search.trim()
        ? requests.filter(r =>
            r.subject_name?.toLowerCase().includes(search.toLowerCase()) ||
            r.cycle_name?.toLowerCase().includes(search.toLowerCase())
          )
        : requests;

    return (
        <div className="flex flex-col gap-20 pb-30">
            <Breadcrumb title="Team 360 Reviews" path="Performance" />

            {/* Filters */}
            <div className="flex flex-wrap gap-10">
                <div className="form-group relative flex-1 min-w-[200px]">
                    <IconSearch size={13} className="absolute top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" style={{ left: '10px' }} />
                    <input
                        type="text"
                        placeholder="Search by name or cycle..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ paddingLeft: '28px' }}
                    />
                </div>
                <div className="form-group min-w-[180px]">
                    <input
                        type="text"
                        placeholder="Filter by cycle..."
                        value={cycleName}
                        onChange={e => setCycleName(e.target.value)}
                    />
                </div>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="py-40 text-center text-text-secondary">
                    <IconLoader className="animate-spin inline-block mb-8" size={20} />
                    <p className="text-sm">Loading...</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className="bg-bg-dark rounded-lg border border-border shadow-light py-50 text-center">
                    <div className="w-44 h-44 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-12">
                        <IconMessageCircle className="text-primary" size={20} />
                    </div>
                    <p className="text-sm font-semibold text-text-primary mb-4">No team 360 reviews found</p>
                    <p className="text-[11px] text-text-secondary">Team members' reviews will appear here once they start a cycle.</p>
                </div>
            ) : (
                <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                    {filtered.map((req, i) => {
                        const progressPct = req.total_nominations > 0
                            ? Math.round((req.responded_count / req.total_nominations) * 100)
                            : 0;
                        return (
                            <button
                                key={req.id}
                                onClick={() => navigate(`/performance/360-feedback/request/${req.id}`)}
                                className={`w-full flex items-center gap-12 px-15 py-10 text-left hover:bg-bg-light transition-colors group ${i > 0 ? 'border-t border-border' : ''}`}
                            >
                                <div
                                    className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                                    style={{ backgroundColor: getAvatarColor(req.subject_name) }}
                                >
                                    {getInitials(req.subject_name)}
                                </div>

                                <div className="flex-grow min-w-0">
                                    <p className="text-[12px] font-semibold text-text-primary truncate leading-tight">{req.subject_name}</p>
                                    <p className="text-[10px] text-text-secondary truncate mt-1">{req.cycle_name}</p>
                                    <div className="flex items-center gap-8 mt-5">
                                        <div className="h-[3px] w-[80px] bg-bg-light rounded-full overflow-hidden">
                                            <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
                                        </div>
                                        <span className="text-[10px] text-text-secondary whitespace-nowrap">
                                            {req.responded_count ?? 0}/{req.total_nominations ?? 0}
                                            {req.deadline ? ` · Due ${formatDate(req.deadline)}` : ''}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-8 flex-shrink-0">
                                    <StatusPill status={req.status} />
                                    <IconChevronRight size={13} className="text-text-secondary group-hover:text-primary transition-colors" />
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
